/**
 * NOTIFICATION_KIND_CONFIG — canonical source of truth for notification kind
 * display labels, icons, and deep-link derivation.
 *
 * Adding a new notification kind requires only an entry here (plus adding it
 * to `KNOWN_KINDS_SET` if it is not a fallback). Unknown kinds fall back to
 * the `unknown` entry, which uses the raw kind string as the label and "info"
 * as the icon.
 *
 * The server-side `KNOWN_KINDS_SET` in `convex/notifications.ts` (T303) must
 * be kept in sync with the five non-fallback kinds exported here.
 *
 * @see specs/prd.md §3 FR-006
 */
import type { NotificationKindEntry } from "../types/notification";

/**
 * Safely extracts a string field from a nullable payload object.
 * Returns `null` if the payload is null/undefined or the field is missing/non-string.
 */
function extractPayloadString(
  payload: Record<string, unknown> | null,
  field: string,
): string | null {
  if (payload == null) return null;
  const value = payload[field];
  return typeof value === "string" ? value : null;
}

/**
 * Configuration map for the six notification kinds.
 *
 * Five are concrete event classes; `unknown` is the fallback for
 * unrecognized kinds (the diagnostic is server-side via
 * `notifications.unknown_kind` log event).
 */
export const NOTIFICATION_KIND_CONFIG: Record<string, NotificationKindEntry> = {
  refinement_complete: {
    label: "Refinement complete",
    icon: "check-circle",
    navigateHref: (payload) => {
      const runId = extractPayloadString(payload, "runId");
      return runId != null ? `#/refine/${runId}` : null;
    },
  },

  refinement_failed: {
    label: "Refinement failed",
    icon: "x-circle",
    navigateHref: (payload) => {
      const runId = extractPayloadString(payload, "runId");
      return runId != null ? `#/refine/${runId}` : null;
    },
  },

  task_assigned: {
    label: "Task assigned",
    icon: "user-plus",
    navigateHref: (payload) => {
      const taskId = extractPayloadString(payload, "taskId");
      const projectId = extractPayloadString(payload, "projectId");
      if (taskId != null && projectId != null) {
        return `#/project/${projectId}/task/${taskId}`;
      }
      return null;
    },
  },

  webhook_failed: {
    label: "Webhook failed",
    icon: "alert-triangle",
    navigateHref: (payload) => {
      const webhookId = extractPayloadString(payload, "webhookId");
      return webhookId != null ? `#/webhooks/${webhookId}` : null;
    },
  },

  subscription_expiring: {
    label: "Subscription expiring",
    icon: "clock",
    navigateHref: () => null,
  },

  unknown: {
    label: "",
    icon: "info",
    navigateHref: () => null,
  },
};

/**
 * The five non-fallback notification kinds.
 *
 * Used by the server-side `listForUser` (T303) to emit a
 * `notifications.unknown_kind` log event for any item whose `kind`
 * is absent from this set.
 *
 * The `unknown` fallback kind is intentionally excluded — it IS the
 * catch-all, not a recognized event class.
 */
export const KNOWN_KINDS_SET: ReadonlySet<string> = new Set([
  "refinement_complete",
  "refinement_failed",
  "task_assigned",
  "webhook_failed",
  "subscription_expiring",
]);
